import * as React from 'react';
// Import material ui modules
import {
	ArrowForward,
	ArrowBack,
	Check,
	DescriptionTwoTone,
} from '@mui/icons-material';
// Import hooks.
import {
	useFormRef,
	UseFormRefResults,
} from '@app/hooks/view';
import useListMortgagee, {
	UseListMortgageeResults
} from './UseListMortgagee';
// Import props.
import {
	FormPopupConfirmationProps,
	ListMortgageesProps,
	ListProps,
	NavigationFooterProps,
	ScrollbarProps,
	StatusProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
} from '@app/enumerations';

interface UseListMortgageesParameters {
	id: string;
	props: ListMortgageesProps,
}

interface UseListMortgageesResults {
	mortgageeList: UseListMortgageeResults;
	applyFormProps: () => ListProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applyAddMortgageeFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyEditMortgageeFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyDeleteMortgageeFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyConfirmNoMortgageesFormPopupConfirmationProps: () => FormPopupConfirmationProps;
	applyValidateMortgageesFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useListMortgagees = (params: UseListMortgageesParameters): UseListMortgageesResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): ListProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Mortgagees',
			emptyBackgroundIcon: DescriptionTwoTone,
			emptyBackgroundMessage: 'There are no mortgagees',
			onAddClick: params.props.allowEdits
				? addMortgagee
				: undefined,
			onRefreshClick: params.props.onRefreshMortgagees,
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			statusProps: applyStatusProps(),
			...formRef.applyProps(),
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isConfirmationOnNoMortgageesRequired: boolean = isConfirmNoMortgageesRequired();
		let isValidationForMortgageesRequired: boolean = isMortgageesValidationRequired();

		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isConfirmationOnNoMortgageesRequired
				? 'Confirm No Mortgagee'
				: (isValidationForMortgageesRequired
					? 'Resubmit'
					: 'Continue'),
			rightButtonClick: isConfirmationOnNoMortgageesRequired
				? confirmNoMortgagees
				: (isValidationForMortgageesRequired
					? validateMortgagees
					: params.props.onForward),
			rightButtonIcon: isConfirmationOnNoMortgageesRequired || isValidationForMortgageesRequired
				? Check
				: ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the add mortgagee confirmation popup form. 
	 **/
	const applyAddMortgageeFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openAddMortgageeConfirmationPopup,
			onClose: cancelConfirmAddMortgagee,
			onConfirm: confirmAddMortgagee,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the edit mortgagee confirmation popup form. 
	 **/
	const applyEditMortgageeFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openEditMortgageeConfirmationPopup,
			onClose: cancelConfirmEditMortgagee,
			onConfirm: confirmEditMortgagee,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the delete mortgagee confirmation popup form. 
	 **/
	const applyDeleteMortgageeFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openDeleteMortgageeConfirmationPopup,
			onClose: cancelConfirmDeleteMortgagee,
			onConfirm: confirmDeleteMortgagee,
			message: params.props.confirmationDeleteMortgageeMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the confirm no mortgagees confirmation popup form. 
	 **/
	const applyConfirmNoMortgageesFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openConfirmNoMortgageeConfirmationPopup,
			onClose: cancelConfirmConfirmNoMortgagees,
			onConfirm: confirmConfirmNoMortgagees,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	/**
	 * Returns props for the validate mortgagees confirmation popup form. 
	 **/
	const applyValidateMortgageesFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openValidateMortgageesConfirmationPopup,
			onClose: cancelConfirmValidateMortgagees,
			onConfirm: confirmValidateMortgagees,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	// #region add mortgagee

	/**
	 *  Starts the add mortgagee process.
	 **/
	const addMortgagee = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenAddMortgageeConfirmationPopup(true);
		}
		else {
			params.props.onAddMortgagee();
		}
	}

	/**
	 * Confirms mortgagee add if user chooses to.
	 **/
	const confirmAddMortgagee = (): void => {
		setOpenAddMortgageeConfirmationPopup(false);
		params.props.onAddMortgagee();
	}

	/**
	 * Cancels the confirmation prompt for adding a mortgagee. 
	 **/
	const cancelConfirmAddMortgagee = (): void => {
		setOpenAddMortgageeConfirmationPopup(false);
	}

	// #endregion add mortgagee

	// #region edit mortgagee

	/**
	 *  Starts the edit mortgagee process.
	 **/
	const editMortgagee = (mortgageeGuid: string): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenEditMortgageeConfirmationPopup(true);
			setSelectedMortgageeGuid(mortgageeGuid);
		}
		else {
			params.props.onEditMortgagee(mortgageeGuid);
		}
	}

	/**
	 * Confirms mortgagee edit if user chooses to.
	 **/
	const confirmEditMortgagee = (): void => {
		const mortgageeGuid: string | undefined = selectedMortgageeGuid;
		if (mortgageeGuid !== undefined) {
			setOpenEditMortgageeConfirmationPopup(false);
			setSelectedMortgageeGuid(undefined);
			params.props.onEditMortgagee(mortgageeGuid);
		}
	}

	/**
	 * Cancels the confirmation prompt for editing a buidling. 
	 **/
	const cancelConfirmEditMortgagee = (): void => {
		setOpenEditMortgageeConfirmationPopup(false);
		setSelectedMortgageeGuid(undefined);
	}

	// #endregion edit mortgagee

	// #region delete mortgagee

	/**
	 *  Starts the delete mortgagee process.
	 **/
	const deleteMortgagee = (mortgageeGuid: string): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnDeleteMortgagee) {
			setOpenDeleteMortgageeConfirmationPopup(true);
			setSelectedMortgageeGuid(mortgageeGuid);
		}
		else {
			params.props.onDeleteMortgagee(mortgageeGuid);
		}
	}

	/**
	 * Confirms mortgagee delete if user chooses to.
	 **/
	const confirmDeleteMortgagee = (): void => {
		const mortgageeGuid: string | undefined = selectedMortgageeGuid;
		if (mortgageeGuid !== undefined) {
			setOpenDeleteMortgageeConfirmationPopup(false);
			setSelectedMortgageeGuid(undefined);
			params.props.onDeleteMortgagee(mortgageeGuid);
		}
	}

	/**
	 * Cancels the confirmation prompt for deleting a mortgagee. 
	 **/
	const cancelConfirmDeleteMortgagee = (): void => {
		setOpenDeleteMortgageeConfirmationPopup(false);
		setSelectedMortgageeGuid(undefined);
	}

	// #endregion delete mortgagee

	// #region confirm no mortgagees.

	/**
	 *  Starts the confirm no mortgagees process.
	 **/
	const confirmNoMortgagees = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenConfirmNoMortgageeConfirmationPopup(true);
		}
		else {
			params.props.onConfirmNoMortgagees();
		}
	}

	/**
	 * Confirms confirm no mortgagees if user chooses to.
	 **/
	const confirmConfirmNoMortgagees = (): void => {
		setOpenConfirmNoMortgageeConfirmationPopup(false);
		params.props.onConfirmNoMortgagees();
	}

	/**
	 * Cancels the confirmation prompt for confirming no mortgagees. 
	 **/
	const cancelConfirmConfirmNoMortgagees = (): void => {
		setOpenConfirmNoMortgageeConfirmationPopup(false);
	}

	// #endregion confirm no mortgagees.

	// #region validate mortgagees.

	/**
	 *  Starts the validate mortgagees process.
	 **/
	const validateMortgagees = (): void => {
		// Make sure the confirmation popup appears if it's required.
		if (requireConfirmationOnSave) {
			setOpenValidateMortgageesConfirmationPopup(true);
		}
		else {
			params.props.onValidateMortgagees();
		}
	}

	/**
	 * Confirms validate mortgagees if user chooses to.
	 **/
	const confirmValidateMortgagees = (): void => {
		setOpenValidateMortgageesConfirmationPopup(false);
		params.props.onValidateMortgagees();
	}

	/**
	 * Cancels the confirmation prompt for validating the mortgagees. 
	 **/
	const cancelConfirmValidateMortgagees = (): void => {
		setOpenValidateMortgageesConfirmationPopup(false);
	}

	// #endregion confirm no mortgagees.

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generates id for mortgagee list item.
	 **/
	const generateIdForMortgageeList = (): string => {
		return `${generateIdForForm()}-mortgageeList`;
	}

	//#endregion id generators

	/**
	 * Checks to see if the form requires confirmation on no mortgagees. 
	 **/
	const isConfirmNoMortgageesRequired = (): boolean => {
		return params.props.allowEdits
			&& params.props.isReviewRequired
			&& params.props.mortgagees.length === 0;
	}

	/**
	 * Checks to see if the form requires mortgagees validation. 
	 **/
	const isMortgageesValidationRequired = (): boolean => {
		return params.props.allowEdits
			&& params.props.isReviewRequired
			&& params.props.mortgagees.length > 0;
	}

	// #endregion private methods


	// #region variables

	let requireConfirmationOnSave: boolean = params.props.requireConfirmationOnSave ?? false;
	let requireConfirmationOnDeleteMortgagee: boolean = params.props.requireConfirmationOnDeleteMortgagee ?? false;

	// Instantiate the form ref hook.
	const formRef: UseFormRefResults = useFormRef();

	// Instantiate the 'mortgagee' list hook.
	const mortgageeList: UseListMortgageeResults = useListMortgagee({
		id: generateIdForMortgageeList(),
		disabled: !params.props.allowEdits,
		mortgagees: params.props.mortgagees,
		onEditMortgagee: editMortgagee,
		onDeleteMortgagee: deleteMortgagee,
	});

	const [selectedMortgageeGuid, setSelectedMortgageeGuid] = React.useState<string | undefined>(undefined);
	const [openAddMortgageeConfirmationPopup, setOpenAddMortgageeConfirmationPopup] = React.useState<boolean>(false);
	const [openEditMortgageeConfirmationPopup, setOpenEditMortgageeConfirmationPopup] = React.useState<boolean>(false);
	const [openDeleteMortgageeConfirmationPopup, setOpenDeleteMortgageeConfirmationPopup] = React.useState<boolean>(false);
	const [openConfirmNoMortgageeConfirmationPopup, setOpenConfirmNoMortgageeConfirmationPopup] = React.useState<boolean>(false);
	const [openValidateMortgageesConfirmationPopup, setOpenValidateMortgageesConfirmationPopup] = React.useState<boolean>(false);
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		if (params.props.mortgagees.length > 0) {
			let mortgageeGuid: string | undefined = params.props.mortgagees[0]?.id ?? undefined;
			if (mortgageeGuid !== undefined) {
				mortgageeList.focusToListItemRecord(mortgageeGuid);
			}
		}
		else {
			formRef.focusToForm();
		}
	}, []);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& params.props.statusMessage !== undefined
			&& params.props.allowEdits) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// #endregion life-cycle methods.


	return {
		mortgageeList: mortgageeList,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
		applyAddMortgageeFormPopupConfirmationProps: applyAddMortgageeFormPopupConfirmationProps,
		applyEditMortgageeFormPopupConfirmationProps: applyEditMortgageeFormPopupConfirmationProps,
		applyDeleteMortgageeFormPopupConfirmationProps: applyDeleteMortgageeFormPopupConfirmationProps,
		applyConfirmNoMortgageesFormPopupConfirmationProps: applyConfirmNoMortgageesFormPopupConfirmationProps,
		applyValidateMortgageesFormPopupConfirmationProps: applyValidateMortgageesFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseListMortgageesParameters,
}

// Export results.
export {
	UseListMortgageesResults,
}

export default useListMortgagees;